import React, { useEffect, useState } from 'react';
import {
    Box,
    Select,
    MenuItem,
    IconButton,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Typography,
    Grid,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    FormControlLabel,
    Radio,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SelectArrow from '../styles/icons/SelectArrow';

const COLOR_SURFACE = '#2C2B2D';
const COLOR_HEADER = '#1E1E1E';
const COLOR_ACCENT = '#0DA3E2';
const DELETE_ICON_BG = "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 6L6 18M6 6L18 18' stroke='%23D35B5F' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";
const createEmptyCondition = (isWhitelist = true, operator = 'AND') => ({
    type: '',
    value: '',
    operator,
    isWhitelist,
});

// Reusable field renderer
const renderField = (fieldConfig, value, onChange, isMainCondition = false) => {
    const labelColor = isMainCondition ? { color: 'white' } : {};

    switch (fieldConfig.type) {
        case "select":
            return (
                <FormControl fullWidth size="small">
                    <InputLabel
                        sx={{
                            ...labelColor,
                            color: COLOR_ACCENT,
                            '&.Mui-focused': { color: COLOR_ACCENT },
                        }}
                    >
                        {fieldConfig.label}
                    </InputLabel>
                    <Select
                        size="small"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        required={fieldConfig.required}
                        IconComponent={SelectArrow}
                        sx={{
                            width: '280px',
                            maxWidth: '100%',
                            height: '56px',
                            color: '#FFFFFF',
                            bgcolor: COLOR_HEADER,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: COLOR_ACCENT,
                                borderRadius: '7px',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: COLOR_ACCENT,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: COLOR_ACCENT,
                            },
                            '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '56px',
                                py: 0,
                                px: '14px',
                            },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    maxHeight: '220px',
                                    overflowY: 'auto',
                                    bgcolor: COLOR_HEADER,
                                    backgroundImage: 'none',
                                    color: '#FFFFFF',
                                    border: `1px solid ${COLOR_ACCENT}`,
                                    borderRadius: '7px',
                                    '&::-webkit-scrollbar': {
                                        width: '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: COLOR_HEADER,
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#FFFFFF',
                                        borderRadius: '3px',
                                    },
                                    '& .MuiMenuItem-root': {
                                        minHeight: 42,
                                        color: '#FFFFFF',
                                        fontSize: '16px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(13,163,226,0.12)',
                                        },
                                        '&.Mui-selected, &.Mui-selected:hover': {
                                            backgroundColor: 'rgba(13,163,226,0.55)',
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        {fieldConfig.options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );

        case "number":
            return (
                <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={fieldConfig.label}
                    inputProps={{ min: fieldConfig.min || 0 }}
                    value={value || ''}
                    onChange={(e) => onChange(parseInt(e.target.value) || 0)}
                    required={fieldConfig.required}
                />
            );

        case "radio":
            console.log(fieldConfig);

            return (
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {fieldConfig.label}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                        {fieldConfig.options.map((op) => (
                            <FormControlLabel
                                key={op.value}
                                control={
                                    <Radio
                                        checked={value === op.value}
                                        onChange={() => onChange(op.value)}
                                        value={op.value}
                                    />
                                }
                                label={op.label}
                            />
                        ))}
                    </Box>
                </Box>
            );

        case "text":
            return (
                <TextField
                    fullWidth
                    size="small"
                    label={fieldConfig.label}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    required={fieldConfig.required}
                />
            );

        default:
            return <Typography>Unknown field type: {fieldConfig.type}</Typography>;
    }
};

const ConditionRow = ({ condition, onUpdate, onDelete, conditionTypes }) => {
    const currentConditionType = conditionTypes[condition.type];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                }}
            >
                <IconButton
                    onClick={onDelete}
                    size="small"
                    sx={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        p: 0,
                        borderRadius: 0,
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            width: 24,
                            height: 24,
                            display: 'inline-block',
                            backgroundImage: DELETE_ICON_BG,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            backgroundSize: 'contain',
                        }}
                    />
                </IconButton>

                <Box sx={{ width: '280px', minWidth: '280px', maxWidth: '280px' }}>
                    {renderField(
                        {
                            type: "select",
                            label: "Condition",
                            options: Object.entries(conditionTypes).map(([key, type]) => ({
                                value: key,
                                label: type.label
                            }))
                        },
                        condition.type,
                        (value) => onUpdate({ ...condition, type: value, value: '', subConditions: {} }),
                        true
                    )}
                </Box>

                <Box sx={{ width: '280px', minWidth: '280px', maxWidth: '280px' }}>
                    {condition.type && currentConditionType ? (
                        renderField(
                            currentConditionType,
                            condition.value,
                            (value) => onUpdate({ ...condition, value }),
                            true
                        )
                    ) : (
                        <FormControl fullWidth size="small">
                            <InputLabel
                                sx={{
                                    color: COLOR_ACCENT,
                                    '&.Mui-focused': { color: COLOR_ACCENT },
                                }}
                            >
                                Value
                            </InputLabel>
                            <Select
                                size="small"
                                IconComponent={SelectArrow}
                                sx={{
                                    width: '280px',
                                    maxWidth: '100%',
                                    height: '56px',
                                    color: '#FFFFFF',
                                    bgcolor: COLOR_HEADER,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: COLOR_ACCENT,
                                        borderRadius: '7px',
                                    },
                                    '& .MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        minHeight: '56px',
                                        py: 0,
                                        px: '14px',
                                    },
                                }}
                            >
                                <MenuItem value="">Select condition first</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </Box>

                <Stack direction="column" alignItems="center" spacing={0.5} sx={{ minWidth: 96 }}>
                    <Switch
                        checked={condition.isWhitelist}
                        onChange={(e) =>
                            onUpdate({ ...condition, isWhitelist: e.target.checked })
                        }
                        inputProps={{ 'aria-label': 'Include / Exclude toggle' }}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: COLOR_ACCENT,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: COLOR_ACCENT,
                                opacity: 1,
                            },
                            '& .MuiSwitch-track': {
                                backgroundColor: '#1E1E1E',
                                opacity: 1,
                            },
                        }}
                    />
                    <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
                        {condition.isWhitelist ? 'Include' : 'Exclude'}
                    </Typography>
                </Stack>
            </Box>

            {condition?.type && currentConditionType?.subConditions?.length > 0 && (
                <Grid container item spacing={2}>
                    {currentConditionType.subConditions.map((sub) => (
                        <Grid item xs={4} key={sub.value}>
                            {renderField(
                                sub,
                                condition.subConditions?.[sub.value],
                                (value) => onUpdate({
                                    ...condition,
                                    subConditions: {
                                        ...condition.subConditions,
                                        [sub.value]: value
                                    }
                                })
                            )}
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

const ConditionBuilder = ({
    value,
    onChange,
    conditionTypes,
    globalMode: controlledGlobalMode,
    onGlobalModeChange,
    hideGlobalToggle = false,
}) => {
    const [conditions, setConditions] = useState(() => {
        if (Array.isArray(value) && value.length > 0) {
            return value;
        }
        return [createEmptyCondition(true, 'AND')];
    });
    const [internalGlobalMode, setInternalGlobalMode] = useState('whitelist');
    const globalMode = controlledGlobalMode ?? internalGlobalMode;

    const handleAddCondition = (operator) => {
        const newCondition = createEmptyCondition(globalMode === 'whitelist', operator);

        const updatedConditions = [...conditions, newCondition];
        setConditions(updatedConditions);
        onChange?.(updatedConditions);
    };

    const handleUpdateCondition = (index, updatedCondition) => {
        const newConditions = [...conditions];
        newConditions[index] = updatedCondition;
        setConditions(newConditions);
        onChange?.(newConditions);
    };

    const handleDeleteCondition = (index) => {
        const filteredConditions = conditions.filter((_, i) => i !== index);
        const safeConditions =
            filteredConditions.length > 0
                ? filteredConditions
                : [createEmptyCondition(globalMode === 'whitelist', 'AND')];
        setConditions(safeConditions);
        onChange?.(safeConditions);
    };

    const applyGlobalMode = (newMode) => {
        if (onGlobalModeChange) {
            onGlobalModeChange(newMode);
        } else {
            setInternalGlobalMode(newMode);
        }

        const updatedConditions = conditions.map((condition) => ({
            ...condition,
            isWhitelist: newMode === 'whitelist',
        }));

        setConditions(updatedConditions);
        onChange?.(updatedConditions);
    };

    const handleToggleAll = (_, requestedMode) => {
        const newMode = requestedMode || (globalMode === 'whitelist' ? 'blacklist' : 'whitelist');
        applyGlobalMode(newMode);
    };

    useEffect(() => {
        if (!Array.isArray(value)) {
            return;
        }
        if (value.length === 0) {
            setConditions([createEmptyCondition(globalMode === 'whitelist', 'AND')]);
            return;
        }
        setConditions(value);
    }, [value]);

    useEffect(() => {
        if (!controlledGlobalMode) {
            return;
        }
        const updatedConditions = conditions.map((condition) => ({
            ...condition,
            isWhitelist: controlledGlobalMode === 'whitelist',
        }));
        setConditions(updatedConditions);
        onChange?.(updatedConditions);
    }, [controlledGlobalMode]);

    return (
        <Box sx={{ width: '100%', color: '#FFFFFF' }}>
            {/* Toggle Button for Whitelist/Blacklist */}
            {!hideGlobalToggle && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <ToggleButtonGroup
                    value={globalMode}
                    exclusive
                    onChange={handleToggleAll}
                    aria-label="Toggle Whitelist/Blacklist"
                    sx={{
                        height: '36px',
                        borderRadius: '4px',
                        border: `1px solid ${COLOR_ACCENT}`,
                        overflow: 'hidden',
                        '& .MuiToggleButtonGroup-grouped': {
                            border: 'none',
                            borderRight: `1px solid ${COLOR_ACCENT}`,
                            borderRadius: '0px',
                            px: '16px',
                            height: '36px',
                            minWidth: '114px',
                            textTransform: 'uppercase',
                            color: '#FFFFFF',
                            fontFamily: 'Assistant, sans-serif',
                            fontWeight: 700,
                            fontSize: '16px',
                            lineHeight: 1,
                            letterSpacing: 0,
                            '&:last-of-type': {
                                borderRight: 'none',
                                borderRadius: '0 4px 4px 0',
                            },
                            '&:first-of-type': {
                                minWidth: '121px',
                                borderRadius: '4px 0 0 4px',
                            },
                            '&.Mui-selected': {
                                backgroundColor: COLOR_ACCENT,
                                color: '#FFFFFF',
                            },
                            '&:not(.Mui-selected)': {
                                backgroundColor: COLOR_HEADER,
                            },
                            '&:hover': {
                                backgroundColor: COLOR_ACCENT,
                                opacity: 0.92,
                            },
                        },
                    }}
                >
                    <ToggleButton value="whitelist">Include All</ToggleButton>
                    <ToggleButton value="blacklist">Exclude All</ToggleButton>
                </ToggleButtonGroup>
                </Box>
            )}

            <Stack spacing={1}>
                {conditions.map((condition, index) => (
                    <Box key={index}>
                        {index > 0 && (
                            <Typography variant="body2" color="textSecondary" align="left" sx={{ my: 1 }}>
                                {condition.operator}
                            </Typography>
                        )}
                        <ConditionRow
                            condition={condition}
                            onUpdate={(updated) => handleUpdateCondition(index, updated)}
                            onDelete={() => handleDeleteCondition(index)}
                            conditionTypes={conditionTypes}
                        />
                    </Box>
                ))}

                {(conditions.length === 0 || conditions.length > 0) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mt: '6px' }}>
                        <Box sx={{ width: 24, minWidth: 24 }} />
                        <Stack direction="row" spacing={2} justifyContent="left">
                            <Button
                                variant="contained"
                                size="small"
                                sx={{
                                    minWidth: '138px',
                                    width: 'fit-content',
                                    height: '36px',
                                    borderRadius: '7px',
                                    border: `1px solid ${COLOR_ACCENT}`,
                                    backgroundColor: COLOR_ACCENT,
                                    px: '10px',
                                    gap: '5px',
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none',
                                    fontFamily: 'Assistant, sans-serif',
                                    fontWeight: 700,
                                    fontSize: '16px',
                                    lineHeight: 1,
                                    letterSpacing: 0,
                                    color: '#FFFFFF',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: 0,
                                        marginRight: '5px',
                                    },
                                    '&:hover': {
                                        backgroundColor: COLOR_ACCENT,
                                        opacity: 0.92,
                                    },
                                }}
                                startIcon={<AddIcon sx={{ color: '#FFFFFF', fontSize: 18 }} />}
                                onClick={() => handleAddCondition('AND')}
                            >
                                AND Condition
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                    minWidth: '138px',
                                    width: 'fit-content',
                                    height: '36px',
                                    borderRadius: '7px',
                                    borderColor: COLOR_ACCENT,
                                    backgroundColor: COLOR_HEADER,
                                    px: '10px',
                                    gap: '5px',
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none',
                                    fontFamily: 'Assistant, sans-serif',
                                    fontWeight: 700,
                                    fontSize: '16px',
                                    lineHeight: 1,
                                    letterSpacing: 0,
                                    color: '#FFFFFF',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: 0,
                                        marginRight: '5px',
                                    },
                                    '&:hover': {
                                        borderColor: COLOR_ACCENT,
                                        backgroundColor: 'rgba(13, 163, 226, 0.08)',
                                    },
                                }}
                                startIcon={<AddIcon sx={{ color: '#FFFFFF', fontSize: 18 }} />}
                                onClick={() => handleAddCondition('OR')}
                            >
                                OR Condition
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};

export default ConditionBuilder;